import React, { useEffect, useMemo, useState } from "react";
import { Search, Filter, MessageSquare, Clock, Download, CheckCircle, AlertTriangle, AlertCircle } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import AdminHeader from "../../Headers/AdminHeader";
import { CategoryEnum, PriorityEnum, RoleEnum, TicketStatusesEnum, ContextEnum } from "../../modules/shared/model";
import { useSelector } from "react-redux";
import {
    getAllTickets,
    useAppDispatch,
    getUserChats,
    getUserConversations,
    getUserSupportTicketNotes,
    EmptyConversations,
} from "../../redux";
import Spinner from "../../modules/shared/components/Spinner";
import { hideLoader, selectLoaderValue, showLoader } from "../../redux/Slices/loadingSlice";
import TicketDetailsModal from "../Modals/TicketDetailsModal";
import TicketCard from "../Modals/TicketCard";
import { webSocketService } from "../../services/websocketService";

const SupportTickets2 = () => {
    // State management
    const isLoading = useSelector(selectLoaderValue);
    const [searchQuery, setSearchQuery] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [selectedPriority, setSelectedPriority] = useState("all");
    const [selectedStatus, setSelectedStatus] = useState("all");
    const [sortBy, setSortBy] = useState("newest");
    const [showTicketDetails, setShowTicketDetails] = useState(null);
    const { allTickets, user, token } = useSelector((state: any) => state.auth);
    const { data, hasNextPage, totalCount } = allTickets;
    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(10);
    const totalPages = Math.ceil(totalCount / limit);
    const dispatch = useAppDispatch();
    
    useEffect(() => {
        getAllCurrentUserTickets(currentPage, limit);
    }, [currentPage, limit]);

    // Real-time WebSocket listener for support ticket messages
    useEffect(() => {
        console.log("====================================");
        console.log("🔧 [ADMIN SUPPORT PAGE] Initializing WebSocket listener");
        console.log("Admin User ID:", user?.id);
        console.log("Token available:", !!token);
        console.log("====================================");

        if (!user?.id) {
            console.warn("⚠️ [ADMIN SUPPORT PAGE] No user ID, skipping WebSocket setup");
            return;
        }

        let subscriptionId: string | null = null;

        const handleWebSocketMessage = (message: any) => {
            console.log("====================================");
            console.log("📨 [ADMIN SUPPORT PAGE] WebSocket message received");
            console.log("Message Type:", message.type);
            console.log("Message Detail:", message.detail);
            console.log("Sender ID:", message.detail?.senderId);
            console.log("Ticket ID:", message.detail?.ticketId);
            console.log("Current Admin User ID:", user.id);
            console.log("Current Ticket Details ID:", showTicketDetails?.id || "null");
            console.log("====================================");
            
            // Handle new support messages
            if (message.type === "new_support_message" || message.type === "support_ticket_update") {
                const { ticketId, chatId, senderId, message: messageText } = message.detail || {};
                
                console.log("✅ [ADMIN SUPPORT PAGE] Message is support-related");
                
                // Check if the message is from a user (not from admin)
                const isFromUser = senderId && senderId !== user.id;
                console.log("Is from user?", isFromUser, "(sender:", senderId, "vs admin:", user.id, ")");
                
                // If we have a selected ticket and it matches the incoming message
                if (showTicketDetails && showTicketDetails.id === ticketId) {
                    console.log("✅ [ADMIN SUPPORT PAGE] Message is for the currently open ticket");
                    console.log("🔄 [ADMIN SUPPORT PAGE] Refreshing conversations for active ticket");
                    console.log("   Using chat ID:", chatId);
                    
                    // Refresh conversations for the current ticket immediately
                    if (chatId) {
                        dispatch(getUserConversations({ id: chatId })).then(() => {
                            console.log("✅ [ADMIN SUPPORT PAGE] Conversations refreshed successfully");
                        }).catch((err) => {
                            console.error("❌ [ADMIN SUPPORT PAGE] Failed to refresh conversations:", err);
                        });
                    } else {
                        console.warn("⚠️ [ADMIN SUPPORT PAGE] No chat ID in message");
                    }
                    
                    console.log("ℹ️ [ADMIN SUPPORT PAGE] Real-time message received - notification handled by backend");
                } else {
                    console.log("ℹ️ [ADMIN SUPPORT PAGE] Message ignored (own message or conditions not met)");
                }
                
                // Refresh the tickets list to update any status/priority changes
                console.log("🔄 [ADMIN SUPPORT PAGE] Refreshing tickets list");
                getAllCurrentUserTickets(currentPage, limit);
            } else {
                console.log("❌ [ADMIN SUPPORT PAGE] Message type not support-related:", message.type);
            }
        };

        // Subscribe to WebSocket messages by connecting with message callback
        console.log("🔌 [ADMIN SUPPORT PAGE] Connecting to WebSocket...");
        console.log("   Using User ID:", user.id);
        console.log("   Token available:", !!token);
        
        webSocketService.connect({
            userId: user.id,
            token: token || "",
            onMessage: (msg) => {
                console.log("🔥 [ADMIN SUPPORT PAGE] CALLBACK FIRED - Message received:", msg.type);
                handleWebSocketMessage(msg);
            },
        }).then((subId) => {
            subscriptionId = subId;
            console.log("✅ [ADMIN SUPPORT PAGE] WebSocket subscription registered:", subId);
            console.log("🔌 [ADMIN SUPPORT PAGE] WebSocket connection status:", webSocketService.getConnectionStatus());
            
            // Test: Send a manual log to confirm subscription is active
            console.log("🧪 [ADMIN SUPPORT PAGE] Subscription test - waiting for messages...");
        }).catch((error) => {
            console.error("❌ [ADMIN SUPPORT PAGE] Failed to register WebSocket subscription:", error);
        });

        return () => {
            if (subscriptionId) {
                webSocketService.removeCallbacksById(subscriptionId);
                console.log("🗑️ [Admin Support] WebSocket subscription removed");
            }
        };
    }, [user?.id, showTicketDetails, currentPage, limit, dispatch, token]);

    const getAllCurrentUserTickets = async (page = 1, limit = 10) => {
        dispatch(showLoader());
        await dispatch(getAllTickets({ page, limit, isShowLoader: false }));
        dispatch(hideLoader());
    };

    const getCurrentChatData = async (ticket: any) => {
        if (!ticket?.id) return;

        dispatch(showLoader());
        try {
            // Get chat data for this ticket
            await dispatch(getUserSupportTicketNotes({ id: ticket.id, isShowLoader: false }));

            const { payload: chatPayload } = await dispatch(getUserChats({ id: ticket.id }));
            if (chatPayload?.id) {
                await dispatch(getUserConversations({ id: chatPayload.id }));
            }

            setShowTicketDetails(ticket);
        } finally {
            dispatch(hideLoader());
        }
    };

    // Mock tickets data
    const filteredTickets = useMemo(() => {
        if (!data || data.length === 0) return [];

        let filtered = [...data];

        // Apply search query filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(
                ticket =>
                    ticket.subject.toLowerCase().includes(query) ||
                    ticket.description.toLowerCase().includes(query) ||
                    `${ticket.userId.firstName} ${ticket.userId.lastName}`.toLowerCase().includes(query)
            );
        }

        // Apply category filter
        if (selectedCategory !== "all") {
            filtered = filtered.filter(ticket => Number(ticket.category) === Number(selectedCategory));
        }

        // Apply priority filter
        if (selectedPriority !== "all") {
            filtered = filtered.filter(ticket => Number(ticket.priority) === Number(selectedPriority));
        }

        // Apply status filter
        if (selectedStatus !== "all") {
            filtered = filtered.filter(ticket => Number(ticket.status) === Number(selectedStatus));
        }

        // Apply sorting
        if (sortBy === "newest") {
            filtered.sort(
                (a, b) => new Date(b.createdAt as string).getTime() - new Date(a.createdAt as string).getTime()
            );
        } else if (sortBy === "oldest") {
            filtered.sort(
                (a, b) => new Date(a.createdAt as string).getTime() - new Date(b.createdAt as string).getTime()
            );
        } else if (sortBy === "priority") {
            filtered.sort((a, b) => Number(b.priority) - Number(a.priority));
        } else if (sortBy === "updated") {
            filtered.sort(
                (a, b) => new Date(b.updatedAt as string).getTime() - new Date(a.updatedAt as string).getTime()
            );
        }

        return filtered.map(ticket => ({
            id: ticket.id,
            user: {
                id: ticket.userId?.id || ticket.userId, // Add user ID for WebSocket and notifications
                name: `${ticket.userId.firstName} ${ticket.userId.lastName}`,
                email: ticket.userId.email,
                phone: ticket.userId.phone,
                type:
                    Number(ticket.userId.role.id) === RoleEnum.client
                        ? "Client"
                        : Number(ticket.userId.role.id) === RoleEnum.professional ||
                          Number(ticket.userId.role.id) === RoleEnum.internationl_professional
                        ? "Professional"
                        : "Service Provider",
                verified: true,
            },
            category: ticket?.category?.id,
            subcategory: "General",
            status: Number(ticket.status?.id),
            priority: Number(ticket.priority?.id),
            subject: ticket.subject,
            description: ticket.description,
            createdAt: ticket.createdAt,
            updatedAt: ticket.updatedAt,
            assignedTo: {
                name: "Support Team",
                email: "support@kokobeo.com",
            },
            responses: [
                // {
                //     id: 1,
                //     type: "user",
                //     content: "Initial ticket submission",
                //     timestamp: ticket.createdAt,
                //     attachments: [],
                // },
            ],
            internalNotes: [
                {
                    id: 1,
                    author: "Admin",
                    content: "Ticket received and being processed",
                    timestamp: ticket.createdAt,
                },
            ],
            attachments: [],
            tags: ["General"],
            history: [
                {
                    action: "Ticket Created",
                    timestamp: ticket.createdAt,
                    by: `${ticket.userId.firstName} ${ticket.userId.lastName}`,
                },
                {
                    action: "Assigned to Support Team",
                    timestamp: ticket.createdAt,
                    by: "System",
                },
            ],
        }));
    }, [data, searchQuery, selectedCategory, selectedPriority, selectedStatus, sortBy]);

    const getCategoryName = (categoryId: number) => {
        switch (Number(categoryId)) {
            case CategoryEnum.Technical:
                return "Technical Support";
            case CategoryEnum.Billing:
                return "Billing Support";
            case CategoryEnum.General:
                return "General Support";
            case CategoryEnum.Payment:
                return "Payment Support";
            default:
                return "General Support";
        }
    };

    const getStatusName = (statusId: number) => {
        return TicketStatusesEnum[statusId] || "Open";
    };

    const getPriorityName = (priorityId: number) => {
        return PriorityEnum[priorityId] || "Medium";
    };

    // Priority levels and their corresponding styles
    const priorityConfig = {
        [PriorityEnum.Low]: {
            color: "bg-blue-100 text-blue-700",
            icon: <Clock className="h-4 w-4" />,
        },
        [PriorityEnum.Medium]: {
            color: "bg-yellow-100 text-yellow-700",
            icon: <AlertCircle className="h-4 w-4" />,
        },
        [PriorityEnum.High]: {
            color: "bg-red-100 text-red-700",
            icon: <AlertTriangle className="h-4 w-4" />,
        },
    };

    // Status configurations
    const statusConfig = {
        [TicketStatusesEnum.Open]: {
            color: "bg-green-100 text-green-700",
            label: "Open",
        },
        [TicketStatusesEnum.In_Progress]: {
            color: "bg-blue-100 text-blue-700",
            label: "In Progress",
        },
        [TicketStatusesEnum.Resolved]: {
            color: "bg-gray-100 text-gray-700",
            label: "Resolved",
        },
        [TicketStatusesEnum.Archived]: {
            color: "bg-yellow-100 text-yellow-700",
            label: "Archived",
        },
        [TicketStatusesEnum.Escalate]: {
            color: "bg-red-100 text-red-700",
            label: "Escalated",
        },
    };

    return (
        <>
            {isLoading && (
                <div className="absolute top-[50%] left-[50%]">
                    <Spinner color="gray" />
                </div>
            )}
            <div
                className={`min-h-screen bg-gray-50 flex flex-col ${isLoading ? "opacity-25" : "opacity-100"}`}
                style={{ textAlign: "left" }}>
                <AdminHeader />
                <main className="flex-1 lg:pl-64 pt-16">
                    <div className="p-6">
                        <div className="max-w-7xl mx-auto space-y-6">
                            {/* Header */}
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                <div>
                                    <h1 className="text-2xl font-bold text-gray-900">Support Tickets</h1>
                                    <p className="text-gray-600 mt-1">Manage and respond to customer support tickets</p>
                                </div>
                                <div className="flex gap-3">
                                    <Button variant="outline" className="flex items-center gap-2">
                                        <Download className="h-4 w-4" />
                                        Export Tickets
                                    </Button>
                                </div>
                            </div>

                            {/* Stats Cards */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                <Card className="p-6">
                                    <div className="flex justify-between items-start">
                                        <div className="space-y-1">
                                            <p className="text-sm text-gray-500">Open Tickets</p>
                                            <p className="text-2xl font-semibold">
                                                {
                                                    filteredTickets.filter(
                                                        ticket => Number(ticket.status) === TicketStatusesEnum.Open
                                                    ).length
                                                }
                                            </p>
                                        </div>
                                        <div className="p-2 bg-blue-100 rounded-lg">
                                            <MessageSquare className="h-5 w-5 text-blue-600" />
                                        </div>
                                    </div>
                                </Card>

                                <Card className="p-6">
                                    <div className="flex justify-between items-start">
                                        <div className="space-y-1">
                                            <p className="text-sm text-gray-500">High Priority</p>
                                            <p className="text-2xl font-semibold">
                                                {
                                                    filteredTickets.filter(
                                                        ticket => Number(ticket.priority) === PriorityEnum.High
                                                    ).length
                                                }
                                            </p>
                                        </div>
                                        <div className="p-2 bg-red-100 rounded-lg">
                                            <AlertTriangle className="h-5 w-5 text-red-600" />
                                        </div>
                                    </div>
                                </Card>

                                <Card className="p-6">
                                    <div className="flex justify-between items-start">
                                        <div className="space-y-1">
                                            <p className="text-sm text-gray-500">Response Time</p>
                                            <p className="text-2xl font-semibold">2.5h</p>
                                        </div>
                                        <div className="p-2 bg-green-100 rounded-lg">
                                            <Clock className="h-5 w-5 text-green-600" />
                                        </div>
                                    </div>
                                </Card>

                                <Card className="p-6">
                                    <div className="flex justify-between items-start">
                                        <div className="space-y-1">
                                            <p className="text-sm text-gray-500">Resolution Rate</p>
                                            <p className="text-2xl font-semibold">0</p>
                                        </div>
                                        <div className="p-2 bg-purple-100 rounded-lg">
                                            <CheckCircle className="h-5 w-5 text-purple-600" />
                                        </div>
                                    </div>
                                </Card>
                            </div>

                            {/* Search and Filters */}
                            <Card className="p-4">
                                <div className="flex flex-col lg:flex-row justify-between gap-4">
                                    <div className="flex flex-col sm:flex-row items-center gap-4">
                                        <div className="relative flex-1 w-full sm:min-w-[300px]">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                            <input
                                                type="text"
                                                placeholder="Search tickets..."
                                                value={searchQuery}
                                                onChange={e => setSearchQuery(e.target.value)}
                                                className="w-full pl-10 pr-4 py-2 border rounded-lg"
                                            />
                                        </div>
                                        <Button
                                            variant="outline"
                                            onClick={() => setShowFilters(!showFilters)}
                                            className="flex items-center gap-2 w-full sm:w-auto">
                                            <Filter className="h-5 w-5" />
                                            Filters
                                        </Button>
                                    </div>

                                    <div className="flex flex-col sm:flex-row items-center gap-4 w-full lg:w-auto">
                                        <select
                                            value={selectedCategory}
                                            onChange={e => setSelectedCategory(e.target.value)}
                                            className="px-4 py-2 border rounded-lg w-full sm:w-auto">
                                            <option value="all">All Categories</option>
                                            {Object.keys(CategoryEnum)
                                                .filter(key => isNaN(Number(key)))
                                                .map(key => (
                                                    <option key={key} value={CategoryEnum[key]}>
                                                        {key.replace("Support", "")}
                                                    </option>
                                                ))}
                                        </select>

                                        <select
                                            value={selectedPriority}
                                            onChange={e => setSelectedPriority(e.target.value)}
                                            className="px-4 py-2 border rounded-lg w-full sm:w-auto">
                                            <option value="all">All Priority</option>
                                            {Object.keys(PriorityEnum)
                                                .filter(key => isNaN(Number(key)))
                                                .map(key => (
                                                    <option key={key} value={PriorityEnum[key]}>
                                                        {key}
                                                    </option>
                                                ))}
                                        </select>

                                        <select
                                            value={selectedStatus}
                                            onChange={e => setSelectedStatus(e.target.value)}
                                            className="px-4 py-2 border rounded-lg w-full sm:w-auto">
                                            <option value="all">All Status</option>
                                            {Object.keys(TicketStatusesEnum)
                                                .filter(key => isNaN(Number(key)))
                                                .map(key => (
                                                    <option key={key} value={TicketStatusesEnum[key]}>
                                                        {key.replace("_", " ")}
                                                    </option>
                                                ))}
                                        </select>

                                        <select
                                            value={sortBy}
                                            onChange={e => setSortBy(e.target.value)}
                                            className="px-4 py-2 border rounded-lg w-full sm:w-auto">
                                            <option value="newest">Newest First</option>
                                            <option value="oldest">Oldest First</option>
                                            <option value="priority">Priority</option>
                                            <option value="updated">Last Updated</option>
                                        </select>
                                    </div>
                                </div>
                            </Card>

                            {/* Tickets Grid */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {filteredTickets.map(ticket => (
                                    <TicketCard
                                        key={ticket.id}
                                        ticket={ticket}
                                        statusConfig={statusConfig}
                                        getStatusName={getStatusName}
                                        priorityConfig={priorityConfig}
                                        getPriorityName={getPriorityName}
                                        setShowTicketDetails={() => getCurrentChatData(ticket)}
                                        getCategoryName={getCategoryName}
                                    />
                                ))}
                            </div>

                            {/* Pagination */}
                            <div className="flex justify-between items-center">
                                <p className="text-sm text-gray-500">
                                    Showing {(currentPage - 1) * limit + 1}-{Math.min(currentPage * limit, totalCount)}{" "}
                                    of {totalCount} tickets
                                </p>
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={currentPage === 1}>
                                        Previous
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setCurrentPage(prev => prev + 1)}
                                        disabled={currentPage === totalPages || !hasNextPage}>
                                        Next
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>

                {/* Ticket Details Modal */}
                <TicketDetailsModal
                    ticket={showTicketDetails}
                    isOpen={!!showTicketDetails}
                    priorityConfig={priorityConfig}
                    getStatusName={getStatusName}
                    statusConfig={statusConfig}
                    getCategoryName={getCategoryName}
                    getPriorityName={getPriorityName}
                    onTicketUpdate={() => getAllCurrentUserTickets(currentPage, limit)}
                    onClose={() => {
                        setShowTicketDetails(null);
                        dispatch(EmptyConversations());
                    }}
                />
            </div>
        </>
    );
};
export default SupportTickets2;
